// src/pages/assessments/AssessmentFormPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import Layout from "../../components/layout/Layout";
import { useBackNavigation } from "../../hooks/useBackNavigation";
import { useBreadcrumb } from "../../context/BreadcrumbContext";
import DocumentManager from "../../components/documents/DocumentManager";
import {
  getAssessmentTypes,
  getAssessmentParentDetails,
  createAssessment,
  updateAssessment,
} from "../../api/assessmentApi";
import { getPropertyById } from "../../api/unitApi";

import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Snackbar,
} from "@mui/material";

function AssessmentFormPage() {
  const { propertyId, unitId, assessmentId } = useParams();
  const navigate = useNavigate();
  const { setCustomPath, clearCustomPath } = useBreadcrumb();

  const isEdit = Boolean(assessmentId);
  const isUnit = Boolean(unitId);

  // Determine the back path based on context
  const backPath = isUnit
    ? `/units/${unitId}/assessments`
    : `/properties/${propertyId}/assessments`;
  const goBack = useBackNavigation(backPath);

  const [form, setForm] = useState({
    assessment_type_id: "",
    pass: false,
    assessment_date: "",
    expiry_date: "",
  });
  const [resolvedPropertyId, setResolvedPropertyId] = useState(propertyId || "");

  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Load breadcrumbs
  useEffect(() => {
    const loadBreadcrumbs = async () => {
      try {
        const data = await getAssessmentParentDetails(propertyId, unitId);
        const actionLabel = isEdit ? "Edit" : "Create";

        if (isUnit) {
          const propertyData = await getPropertyById(data.unit.property_id);
          setResolvedPropertyId(data.unit.property_id);
          setCustomPath([
            { label: "Properties", path: "/properties" },
            { label: propertyData.name, path: `/properties/${data.unit.property_id}/details` },
            {
              label: `Unit ${data.unit.unit_name || data.unit.unit_code || data.unit.id}`,
              path: `/units/${unitId}/details`,
            },
            { label: "Assessments", path: `/units/${unitId}/assessments` },
            { label: actionLabel, path: null },
          ]);
        } else {
          setResolvedPropertyId(propertyId);
          setCustomPath([
            { label: "Properties", path: "/properties" },
            { label: data.name, path: `/properties/${propertyId}/details` },
            { label: "Assessments", path: `/properties/${propertyId}/assessments` },
            { label: actionLabel, path: null },
          ]);
        }
      } catch (err) {
        console.error("Failed to load breadcrumbs:", err);
      }
    };

    loadBreadcrumbs();
    return () => clearCustomPath();
  }, [propertyId, unitId, isUnit, isEdit, setCustomPath, clearCustomPath]);

  // Load types
  useEffect(() => {
    const loadTypes = async () => {
      try {
        const data = await getAssessmentTypes();
        setAssessmentTypes(data);
      } catch {
        setAssessmentTypes([
          { id: 1, name: "Fire Safety Assessment" },
          { id: 2, name: "Gas Safety Inspection" },
          { id: 3, name: "Asbestos Survey" },
          { id: 4, name: "Insurance Risk Assessment" },
        ].sort((a, b) => a.name.localeCompare(b.name)));
      }
    };

    loadTypes();
  }, []);

  // Load existing assessment
  useEffect(() => {
    if (!isEdit) return;

    const loadAssessment = async () => {
      try {
        const data = await getAssessmentParentDetails(propertyId, unitId);
        const existing = data.assessments?.find(
          (a) => a.id === parseInt(assessmentId)
        );

        if (existing) {
          setForm({
            assessment_type_id: existing.assessment_type_id || "",
            pass: existing.passed ?? false,
            assessment_date: existing.assessment_date || "",
            expiry_date: existing.expiry_date || "",
          });
        }
      } catch {
        setError("Failed to load assessment.");
      }
    };

    loadAssessment();
  }, [isEdit, propertyId, unitId, assessmentId]);

  useEffect(() => {
    if (isEdit) return;
    const nextYear = new Date();
    nextYear.setFullYear(nextYear.getFullYear() + 1);
    const defaultExpiry = nextYear.toISOString().split("T")[0];
    setForm((prev) => ({
      ...prev,
      expiry_date: prev.expiry_date || defaultExpiry,
    }));
  }, [isEdit]);

  // Handle form change
  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (
      form.assessment_date &&
      form.expiry_date &&
      form.expiry_date <= form.assessment_date
    ) {
      setError("Expiry date must be after assessment date.");
      return;
    }

    const payload = {
      ...form,
      property_id: propertyId || null,
      unit_id: unitId || null,
    };

    try {
      if (isEdit) {
        await updateAssessment(assessmentId, payload);
      } else {
        await createAssessment(payload);
      }

      navigate(
        isUnit
          ? `/units/${unitId}/assessments`
          : `/properties/${propertyId}/assessments`,
        {
          state: {
            toast: {
              message: isEdit ? "Assessment updated" : "Assessment created",
              severity: "success",
            },
          },
        }
      );
    } catch (err) {
      console.error(err);
      setToast({
        open: true,
        message: "Failed to save assessment.",
        severity: "error",
      });
    }
  };

  // -----------------------------------------------------
  // UI
  // -----------------------------------------------------
  return (
    <Layout>
      <Box className="page-container">
        <Paper sx={{ p: 4, width: "100%" }} elevation={2}>
          <Typography variant="h5" fontWeight="600" sx={{ mb: 2 }}>
            {isEdit
              ? `Edit ${isUnit ? "Unit" : "Property"} Assessment`
              : `Add ${isUnit ? "Unit" : "Property"} Assessment`}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Assessment Type */}
              <Grid item xs={12}>
                <FormControl fullWidth required sx={{ minWidth: 260 }}>
                  <InputLabel>Assessment Type</InputLabel>
                  <Select
                    name="assessment_type_id"
                    label="Assessment Type"
                    value={form.assessment_type_id}
                    onChange={handleChange}
                  >
                    {assessmentTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Pass Checkbox */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.pass}
                      name="pass"
                      onChange={handleChange}
                    />
                  }
                  label="Passed Assessment"
                />
              </Grid>

              {/* Dates */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Assessment Date"
                  name="assessment_date"
                  type="date"
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={form.assessment_date}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Expiry Date"
                  name="expiry_date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={form.expiry_date}
                  onChange={handleChange}
                  error={Boolean(
                    form.assessment_date &&
                    form.expiry_date &&
                    form.expiry_date <= form.assessment_date
                  )}
                  helperText={
                    form.assessment_date &&
                    form.expiry_date &&
                    form.expiry_date <= form.assessment_date
                      ? "Expiry date must be after assessment date."
                      : ""
                  }
                />
              </Grid>

              {/* Buttons */}
              <Grid item xs={12} sx={{ display: "flex", gap: 2, mt: 2 }}>
                <Button type="submit" variant="contained">
                  {isEdit ? "Update Assessment" : "Create Assessment"}
                </Button>

                <Button variant="outlined" onClick={() => goBack()}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <DocumentManager
          propertyId={resolvedPropertyId}
          targetType="assessment"
          targetId={assessmentId}
        />
      </Box>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default AssessmentFormPage;
